import React from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { AiStatusProvider } from "./contexts/AiStatusContext";
import { Toaster } from "@/components/ui/sonner";

import Header from "./components/Header";

// Set axios defaults globally
axios.defaults.withCredentials = true;

// Capture a referral code from ?ref= on first load so signup can attribute it,
// and count the open once per browser (QR/link reach metric).
try {
  const _ref = new URLSearchParams(window.location.search).get('ref');
  if (_ref) {
    localStorage.setItem('kolv_ref', _ref);
    if (localStorage.getItem('kolv_ref_pinged') !== _ref) {
      localStorage.setItem('kolv_ref_pinged', _ref);
      axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/referrals/visit`, { code: _ref }).catch(() => {});
    }
  }
} catch (e) { /* ignore */ }

import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import InvestSuccessPage from "./pages/InvestSuccessPage";
import InvestPage from "./pages/InvestPage";
import InvestmentTermsPage from "./pages/InvestmentTermsPage";
import WelcomeOnboarding from "./pages/WelcomeOnboarding";
import RegisterPage from "./pages/RegisterPage";
import ListingsPage from "./pages/ListingsPage";
import ListingDetailPage from "./pages/ListingDetailPage";
import CreateListingPage from "./pages/CreateListingPage";
import MyListingsPage from "./pages/MyListingsPage";
import ProfilePage from "./pages/ProfilePage";
import MessagesPage from "./pages/MessagesPage";
import ShopProfilePage from "./pages/ShopProfilePage";
import BusinessTemplatesPage from "./pages/BusinessTemplatesPage";
import StaffBusinessPage from "./pages/StaffBusinessPage";
import BusinessPublicPage from "./pages/BusinessPublicPage";
import BusinessDemoPage from "./pages/BusinessDemoPage";
import PremiumDemoPage from "./pages/PremiumDemoPage";
import ShopOnboardingPage from "./pages/ShopOnboardingPage";
import BlogIndexPage from "./pages/BlogIndexPage";
import BlogPostPage from "./pages/BlogPostPage";
import ShopPage from "./pages/ShopPage";
import AdminPage from "./pages/AdminPage";
import FavoritesPage from "./pages/FavoritesPage";
import AnalyticsDashboard from "./pages/AnalyticsDashboard";
import CatalogPage from "./pages/CatalogPage";
import CatalogDetailPage from "./pages/CatalogDetailPage";
import ShopCollectionsPage from "./pages/ShopCollectionsPage";
import CartPage from "./pages/CartPage";
import WalletPage from "./pages/WalletPage";
import UnifiedProfilePage from "./pages/BuyerProfilePage";
import OrdersPage from "./pages/OrdersPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import OrderSuccessPage from "./pages/OrderSuccessPage";
import OrderStatusPage from "./pages/OrderStatusPage";
import KitchenScreen from "./pages/KitchenScreen";
import GiftCardPage from "./pages/GiftCardPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfUsePage from "./pages/TermsOfUsePage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import RegionPage from "./pages/RegionPage";
import MapPage from "./pages/MapPage";
import SellerUpgradePage from "./pages/SellerUpgradePage";
import FeeSchedulePage from "./pages/FeeSchedulePage";
import GigsPage from "./pages/GigsPage";
import GigDetailPage from "./pages/GigDetailPage";
import CreateGigPage from "./pages/CreateGigPage";
import ProProfilePage from "./pages/ProProfilePage";
// Legacy profile URLs (/pro/:id, /buyer/:id) now redirect to the unified profile.
function RedirectToUnified() {
  const { userId } = useParams();
  return <Navigate to={`/lietotajs/${userId}`} replace />;
}
import EventsPage from "./pages/EventsPage";
import CreateEventPage from "./pages/CreateEventPage";
import ThemedShopPage from "./pages/ThemedShopPage";
import ApiDocsPage from "./pages/ApiDocsPage";
import EventDetailPage from "./pages/EventDetailPage";
import CalendarPage from "./pages/CalendarPage";
import NotificationsPage from "./pages/NotificationsPage";
import ClassifiedsPage from "./pages/ClassifiedsPage";
import ClassifiedDetailPage from "./pages/ClassifiedDetailPage";
import CreateClassifiedPage from "./pages/CreateClassifiedPage";
import MyClassifiedsPage from "./pages/MyClassifiedsPage";
import ClassifiedDealsPage from "./pages/ClassifiedDealsPage";
import SavedSearchesPage from "./pages/SavedSearchesPage";
import Footer from "./components/Footer";
import CookieConsent from "./components/CookieConsent";
import BottomNav from "./components/BottomNav";

// Resolve a pretty /@nickname link to the user's public profile.
function HandleResolver() {
  const { handle } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    let active = true;
    const raw = decodeURIComponent(handle || "");
    if (!raw.startsWith("@")) { navigate("/", { replace: true }); return; }
    const nick = raw.slice(1).trim();
    if (!nick) { navigate("/", { replace: true }); return; }
    const suffix = (location.search || "") + (location.hash || "");
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/user-by-nickname/${encodeURIComponent(nick)}`)
      .then(({ data }) => { if (active) navigate(`/lietotajs/${data.user_id}${suffix}`, { replace: true }); })
      .catch(() => { if (active) navigate("/", { replace: true }); });
    return () => { active = false; };
  }, [handle, location.search, location.hash, navigate]);

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D97736]"></div>
    </div>
  );
}

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D97736]"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

function AppRoutes() {
  return (
    <BrowserRouter>
      <Shell />
      <Toaster />
    </BrowserRouter>
  );
}

function Shell() {
  const { pathname } = useLocation();
  const { loading } = useAuth();

  // Guests and logged-in users can browse the marketplace freely at "/".
  // Onboarding lives at "/welcome" and is entered right after registration.
  // Actions that require an account are gated by <ProtectedRoute>.
  const bare = ['/welcome', '/login', '/register', '/forgot-password', '/reset-password', '/verify-email', '/invest/success', '/virtuve'].includes(pathname) || pathname.startsWith('/business-demo') || pathname.startsWith('/premium-demo') || pathname.startsWith('/uznemums/') || pathname.startsWith('/shop/') || pathname.startsWith('/davanu-karte/');  // full-screen onboarding/auth/demo + standalone business & shop + kitchen + gift-card pages (own chrome), no global nav/footer

  const homeElement = loading
    ? <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D97736]" /></div>
    : <HomePage />;

  return (
    <div className={`min-h-screen bg-[#FAFAFA] overflow-x-hidden ${!bare ? 'pb-[60px] md:pb-0' : ''}`}>
      {!bare && <Header />}
      <main>
        <Routes>
            <Route path="/" element={homeElement} />
            <Route path="/welcome" element={<WelcomeOnboarding />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />            <Route path="/login" element={<LoginPage />} />
            <Route path="/invest/success" element={<InvestSuccessPage />} />
            <Route path="/investet" element={<InvestPage />} />
            <Route path="/investiciju-noteikumi" element={<InvestmentTermsPage />} />
            <Route path="/invest/cancelled" element={<Navigate to="/" replace />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/order/success" element={<OrderSuccessPage />} />
            <Route path="/pasutijums/:code" element={<OrderStatusPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/listings" element={<ListingsPage />} />
            <Route path="/sludinajumi" element={<ClassifiedsPage />} />
            <Route path="/sludinajumi/grozs" element={<Navigate to="/cart" replace />} />
            <Route path="/sludinajumi/darijumi" element={<ProtectedRoute><ClassifiedDealsPage /></ProtectedRoute>} />
            <Route path="/sludinajumi/meklejumi" element={<ProtectedRoute><SavedSearchesPage /></ProtectedRoute>} />
            <Route path="/sludinajumi/jauns" element={<ProtectedRoute><CreateClassifiedPage /></ProtectedRoute>} />
            <Route path="/sludinajumi/:id/labot" element={<ProtectedRoute><CreateClassifiedPage /></ProtectedRoute>} />
            <Route path="/mani-sludinajumi" element={<ProtectedRoute><MyClassifiedsPage /></ProtectedRoute>} />
            <Route path="/sludinajums/:id" element={<ClassifiedDetailPage />} />
            <Route path="/gigs" element={<GigsPage />} />
            <Route
              path="/gigs/create"
              element={
                <ProtectedRoute>
                  <CreateGigPage />
                </ProtectedRoute>
              }
            />
            <Route path="/gigs/:id" element={<GigDetailPage />} />
            <Route path="/lietotajs/:userId" element={<UnifiedProfilePage />} />
            <Route path="/pro/:userId" element={<RedirectToUnified />} />
            <Route path="/uznemums/:userId" element={<BusinessPublicPage />} />
            <Route path="/uznemums/:userId/:pageId" element={<BusinessPublicPage />} />
            <Route path="/business-demo/:template" element={<BusinessDemoPage />} />
            <Route path="/premium-demo/:plan" element={<PremiumDemoPage />} />
            <Route path="/business-templates" element={<ProtectedRoute><BusinessTemplatesPage /></ProtectedRoute>} />
            <Route path="/komanda/:bizId" element={<ProtectedRoute><StaffBusinessPage /></ProtectedRoute>} />
            <Route path="/komanda/:bizId/:pageId" element={<ProtectedRoute><StaffBusinessPage /></ProtectedRoute>} />
            <Route path="/virtuve" element={<ProtectedRoute><KitchenScreen /></ProtectedRoute>} />
            <Route path="/davanu-karte/:code" element={<GiftCardPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route
              path="/events/create"
              element={
                <ProtectedRoute>
                  <CreateEventPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/events/:id/edit"
              element={
                <ProtectedRoute>
                  <CreateEventPage />
                </ProtectedRoute>
              }
            />
            <Route path="/events/:id" element={<EventDetailPage />} />
            <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
            <Route path="/region/:slug" element={<RegionPage />} />
            <Route path="/karte" element={<MapPage />} />
            <Route path="/map" element={<MapPage />} />
            <Route path="/listing/:id" element={<ListingDetailPage />} />
            <Route path="/blog" element={<BlogIndexPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route
              path="/create-listing"
              element={
                <ProtectedRoute>
                  <CreateListingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-listing/:id"
              element={
                <ProtectedRoute>
                  <CreateListingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-listings"
              element={
                <ProtectedRoute>
                  <MyListingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/messages"
              element={
                <ProtectedRoute>
                  <MessagesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/shop-profile"
              element={
                <ProtectedRoute>
                  <ShopProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/shop-onboarding"
              element={
                <ProtectedRoute>
                  <ShopOnboardingPage />
                </ProtectedRoute>
              }
            />
            <Route path="/shop/:userId/theme" element={<ThemedShopPage />} />
            <Route path="/shop/:userId" element={<ShopPage />} />
            <Route path="/developers" element={<ApiDocsPage />} />
            <Route path="/buyer/:userId" element={<RedirectToUnified />} />
            <Route path="/catalog" element={<CatalogPage />} />
            <Route path="/catalog/:catalogId" element={<CatalogDetailPage />} />
            <Route path="/shop-collections" element={<ShopCollectionsPage />} />
            <Route
              path="/cart"
              element={
                <ProtectedRoute>
                  <CartPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders"
              element={
                <ProtectedRoute>
                  <OrdersPage mode="buyer" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/seller/orders"
              element={
                <ProtectedRoute>
                  <OrdersPage mode="seller" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/wallet"
              element={
                <ProtectedRoute>
                  <WalletPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/seller/upgrade-status"
              element={
                <ProtectedRoute>
                  <SellerUpgradePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/favorites"
              element={
                <ProtectedRoute>
                  <FavoritesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute>
                  <AnalyticsDashboard />
                </ProtectedRoute>
              }
            />
            <Route path="/privatuma-politika" element={<PrivacyPolicyPage />} />
            <Route path="/lietosanas-noteikumi" element={<TermsOfUsePage />} />
            <Route path="/sikdatnu-politika" element={<CookiePolicyPage />} />
            <Route path="/maksas-un-komisijas" element={<FeeSchedulePage />} />
            {/* Pretty public-profile handle: /@nickname (kept last so it never shadows a real route). */}
            <Route path="/:handle" element={<HandleResolver />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
        {!bare && pathname !== '/business-templates' && pathname !== '/admin' && <Footer />}
        {!bare && <CookieConsent />}
        {!bare && pathname !== '/business-templates' && pathname !== '/admin' && <BottomNav />}
      </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <AiStatusProvider>
          <AppRoutes />
        </AiStatusProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
